/**
 * Number Input with Percentage/Value Select Renderer
 *
 * Number input with a select box to choose between "$ value" or "% percentage"
 * When percentage is selected, uses a reference field value (like annualSalary) to calculate the dollar value
 */

import React, { useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import type { FieldRenderer } from '@/MyCode/core';

/**
 * Number Input with Percentage/Value Select Renderer
 * Features:
 * - Number input for entering value
 * - Select box to choose between "$ value" or "% percentage"
 * - When "% percentage" is selected, calculates dollar value based on reference field
 * - When "$ value" is selected, stores direct dollar amount
 * - Uses reference field from formData (e.g., annualSalary)
 */
export const renderNumberInputPercentageValue: FieldRenderer = ({
    field,
    value,
    hasError,
    handleChange,
    formData,
    isRequired
}) => {
    // Get reference field name from field config (e.g., "annualSalary")
    const referenceFieldName = (field as any).referenceField;

    // Get reference field value from formData
    const referenceValue = referenceFieldName ? (formData[referenceFieldName] || 0) : 0;

    // Get mode field name (stores whether it's "dollar" or "percentage")
    const modeFieldName = `${field.name}_mode`;
    const mode = formData[modeFieldName] || 'dollar'; // Default to 'dollar'

    // Format number with commas and preserve decimal points
    const formatNumberWithCommas = (numValue: number | string): string => {
        if (numValue === '' || numValue === null || numValue === undefined) return '';

        let numStr: string;
        if (typeof numValue === 'string') {
            // Preserve the string as-is if it's a string (to handle cases like ".5" during typing)
            numStr = numValue;
        } else {
            // Convert number to string, preserving decimals
            numStr = numValue.toString();
        }

        // Handle special case: string starting with just a decimal point (e.g., ".5")
        if (numStr === '.' || numStr.startsWith('.')) {
            // If it's just ".", return it as-is
            if (numStr === '.') {
                return '.';
            }
            // If it starts with ".", preserve it (e.g., ".5" -> ".5")
            const decimalPart = numStr.substring(1).replace(/\D/g, '');
            return '.' + decimalPart;
        }

        // Split into integer and decimal parts
        const parts = numStr.split('.');
        let integerPart = parts[0].replace(/\D/g, ''); // Remove non-digits from integer part
        const decimalPart = parts.length > 1 ? '.' + parts[1].replace(/\D/g, '') : '';

        // If integer part is empty but we have a decimal, return just the decimal part
        if (integerPart === '' && decimalPart) {
            return decimalPart;
        }

        // Format integer part with commas
        if (integerPart) {
            integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }

        return integerPart + decimalPart;
    };

    // Initialize display value
    const [displayValue, setDisplayValue] = useState<string>(() => {
        if (value !== undefined && value !== null && value !== '') {
            const numVal = typeof value === 'number' ? value : Number(value);
            if (!isNaN(numVal)) {
                return formatNumberWithCommas(numVal);
            }
        }
        return '';
    });

    // Ref to track if we're in the middle of a mode change to prevent useEffect from overwriting
    const isModeChangingRef = useRef(false);
    // Ref to track if user is actively typing to prevent auto-conversion
    const isUserTypingRef = useRef(false);

    // Update display value when external value or mode changes
    useEffect(() => {
        // Skip update if we're in the middle of a mode change (we handle it manually)
        if (isModeChangingRef.current) {
            isModeChangingRef.current = false;
            return;
        }

        // Skip update if user is actively typing (to preserve their input like ".5")
        if (isUserTypingRef.current) {
            return;
        }

        if (value !== undefined && value !== null && value !== '') {
            const numericVal = typeof value === 'number' ? value : Number(value);
            if (!isNaN(numericVal)) {
                // Always show the stored value (which is either dollar or percentage depending on mode)
                // For percentage mode, values are stored in 0-100 range (e.g., 0.5 = 0.5%, 50 = 50%)
                // Don't auto-convert - trust the stored value
                setDisplayValue(formatNumberWithCommas(numericVal));
            }
        } else if (value === '' || value === null || value === undefined) {
            setDisplayValue('');
        }
    }, [value, mode]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // Set flag to indicate user is actively typing
        isUserTypingRef.current = true;

        // Clear the flag after a short delay to allow useEffect to run again
        setTimeout(() => {
            isUserTypingRef.current = false;
        }, 100);

        // Remove all non-digit and non-decimal characters
        const sanitized = inputValue.replace(/[^\d.]/g, '');

        // Ensure only one decimal point
        const parts = sanitized.split('.');
        let cleaned = parts[0].replace(/\D/g, '');
        if (parts.length > 1) {
            cleaned += '.' + parts.slice(1).join('').replace(/\D/g, '');
        }

        // Parse as float (allow empty string or just decimal point for user input)
        const numeric = cleaned && cleaned !== '.' ? parseFloat(cleaned) : (cleaned === '.' ? NaN : 0);

        // Clamp percentage values to 0-100 range
        let finalValue = numeric;
        if (mode === 'percentage' && !isNaN(numeric)) {
            finalValue = Math.max(0, Math.min(100, numeric));
            // If value was clamped, update the cleaned string to show the clamped value
            if (finalValue !== numeric) {
                cleaned = finalValue.toString();
            }
        }

        // Format for display - preserve user input like ".5" during typing
        const formatted = formatNumberWithCommas(cleaned || '');
        setDisplayValue(formatted);

        if (!isNaN(numeric)) {
            // If mode is percentage, store the percentage value (0-100 range)
            // e.g., 0.5 = 0.5%, 50 = 50%
            // If mode is dollar, store the dollar value
            handleChange(field.name, finalValue);
        } else if (cleaned === '' || cleaned === '.') {
            // Allow empty or just decimal point during typing
            handleChange(field.name, '');
        }
    };

    const handleModeChange = (newMode: string) => {
        // Convert value between dollar and percentage BEFORE changing mode
        if (value !== undefined && value !== null && value !== '' && referenceValue > 0) {
            // Get the actual numeric value - use displayValue if available to get the user's input
            // Otherwise use the stored value
            let currentValue: number;

            // Try to parse from displayValue first (what user sees/entered)
            const displayNum = displayValue ? parseFloat(displayValue.replace(/,/g, '')) : NaN;
            if (!isNaN(displayNum) && displayNum > 0) {
                currentValue = displayNum;
            } else {
                // Fall back to stored value
                currentValue = typeof value === 'number' ? value : Number(value);
            }

            if (!isNaN(currentValue) && currentValue > 0 && referenceValue > 0) {
                let convertedValue: number;

                if (newMode === 'percentage' && mode === 'dollar') {
                    // Convert dollar to percentage (0-100 range)
                    // Calculate percentage: (dollarAmount / referenceAmount) * 100
                    convertedValue = (currentValue / referenceValue) * 100;
                    // Round to 2 decimal places to avoid floating point issues
                    convertedValue = Math.round(convertedValue * 100) / 100;
                    // Ensure percentage is in 0-100 range
                    convertedValue = Math.max(0, Math.min(100, convertedValue));
                } else if (newMode === 'dollar' && mode === 'percentage') {
                    // Convert percentage (0-100) to dollar
                    // Ensure percentage is in valid range before conversion
                    const clampedPercentage = Math.max(0, Math.min(100, currentValue));
                    convertedValue = (clampedPercentage / 100) * referenceValue;
                    // Round to 2 decimal places for dollar amounts
                    convertedValue = Math.round(convertedValue * 100) / 100;
                } else {
                    // Same mode, no conversion needed
                    convertedValue = currentValue;
                }

                // Set flag to prevent useEffect from overwriting our display update
                isModeChangingRef.current = true;

                // Update display value immediately with formatted converted value
                const formattedConverted = formatNumberWithCommas(convertedValue);
                setDisplayValue(formattedConverted);

                // Update both the value and mode together
                handleChange(field.name, convertedValue);
                handleChange(modeFieldName, newMode);
            } else {
                // No valid value, just update mode
                handleChange(modeFieldName, newMode);
            }
        } else {
            // No value or no reference, just update mode
            handleChange(modeFieldName, newMode);
        }
    };

    // Calculate the effective dollar value for display (when in percentage mode)
    const getEffectiveDollarValue = (): number => {
        if (mode === 'percentage' && value !== undefined && value !== null && value !== '') {
            const numericVal = typeof value === 'number' ? value : Number(value);
            if (!isNaN(numericVal) && referenceValue > 0) {
                return (numericVal / 100) * referenceValue;
            }
        }
        return typeof value === 'number' ? value : (value ? Number(value) : 0);
    };

    const effectiveDollarValue = getEffectiveDollarValue();

    return (
        <div className="space-y-2 w-full">
            <Label htmlFor={field.name}>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div>
                <div className="flex rounded-md shadow-xs">
                    <Input
                        id={field.name}
                        type="text"
                        value={displayValue}
                        onChange={handleInputChange}
                        placeholder={mode === 'percentage' ? '0-100' : (field.placeholder || '0')}
                        max={mode === 'percentage' ? 100 : undefined}
                        className={`flex flex-1 -me-px rounded-e-none shadow-none focus-visible:z-10 ${hasError ? 'border-destructive' : ''}`}
                    />
                    <select
                        value={mode}
                        onChange={(e) => handleModeChange(e.target.value)}
                        className="w-fit rounded-s-nones text-muted-foreground shadow-none hover:text-foreground border border-input bg-background px-3 py-0 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                        <option value="dollar">$ value</option>
                        <option value="percentage">% percentage</option>
                    </select>
                </div>
                {mode === 'percentage' && referenceFieldName && referenceValue > 0 && value && (
                    <p className="text-xs text-muted-foreground mt-2">
                        = ${formatNumberWithCommas(effectiveDollarValue.toFixed(2))} (based on {referenceFieldName}:
                        ${formatNumberWithCommas(referenceValue.toFixed(2))})
                    </p>
                )}
                {/* {mode === 'percentage' && (
                    <p className="text-xs text-muted-foreground">
                        Enter percentage as a value between 0 and 100 (e.g., 10 for 10%)
                    </p>
                )} */}
            </div>
        </div>
    );
};

export default renderNumberInputPercentageValue;

